/**
 * Entitlements from subscription/license (replaces company_config for setup and object manager access).
 * Stored in sessionStorage after login; source: GET /api/auth/entitlements (role's company_license).
 */
export const ENTITLEMENTS_KEY = 'entitlements';

export const getEntitlements = () => {
  if (typeof sessionStorage === 'undefined') return {};
  try {
    const raw = sessionStorage.getItem(ENTITLEMENTS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (e) {
    return {};
  }
};

export const getEntitlementsSection = (sectionKey, defaultValue = null) => {
  const ent = getEntitlements();
  if (!sectionKey) return ent;
  return ent?.[sectionKey] ?? defaultValue;
};

/** Whether the current user can access Setup (from license entitlements). */
export const canAccessSetup = () => {
  const setup = getEntitlementsSection('setup', {});
  return setup.showSetup === true;
};

/**
 * Packages enabled for the current user's subscription (e.g. ['watconnect']).
 * Controlled from admin panel via plan config enabledPackages; resolved from company_subscription / company_license.
 */
export const getEnabledPackages = () => {
  const ent = getEntitlements();
  const packages = Array.isArray(ent?.enabledPackages) ? ent.enabledPackages : [];
  console.log('[entitlements] getEnabledPackages from sessionStorage:', packages, 'raw ent.enabledPackages:', ent?.enabledPackages);
  return packages;
};

/** Whether the current user has the given package (e.g. 'watconnect') from subscription/plan. */
export const hasPackage = (packageKey) => {
  return getEnabledPackages().includes(packageKey);
};

/**
 * True if a main-nav tab points at WatConnect surfaces (paths or package), independent of license.
 * Used to hide these tabs when hasPackage('watconnect') is false — including legacy tabs missing config.package.
 */
export const tabUrlLooksLikeWatconnect = (rawUrl) => {
  if (rawUrl == null || rawUrl === '') return false;
  let u = String(rawUrl).toLowerCase().trim().split('?')[0];
  if (!u.startsWith('/')) u = `/${u}`;
  const path = u.replace(/\/+$/, '') || '/';
  const prefixes = [
    '/watconnect',
    '/campaign',
    '/groups',
    '/greetingmessage',
    '/module',
    '/plan',
    '/addon',
    '/company',
    '/invoice',
    '/wallet',
    '/billing',
    '/chatbot',
    '/tags',
    '/template/add',
    '/template/view',
  ];
  for (const p of prefixes) {
    if (path === p || path.startsWith(`${p}/`)) return true;
  }
  return false;
};

export const isWatconnectNavTab = (tab) => {
  if (!tab || typeof tab !== 'object') return false;
  const pkg = String(tab?.config?.package ?? tab?.package ?? '')
    .toLowerCase()
    .trim();
  if (pkg === 'watconnect') return true;
  const obj = String(tab?.object_name ?? '')
    .toLowerCase()
    .trim();
  if (obj.startsWith('watconnect_') || obj === 'whatsapp_setting') return true;
  if (tabUrlLooksLikeWatconnect(tab?.url)) return true;
  return false;
};

/**
 * Tab IDs allowed for the current user (from company_license).
 * When non-empty, the menu can filter by id (fallback).
 */
export const getAllowedTabIds = () => {
  const ent = getEntitlements();
  const tabSection = ent?.tab;
  const ids = Array.isArray(tabSection?.allowedTabs) ? tabSection.allowedTabs : [];
  return ids.filter((id) => id);
};

/**
 * Tab names allowed by the license (from company_license → getStandardTabs → names in session).
 * When non-empty, only show tabs whose name/label/object_name is in this list.
 */
export const getAllowedTabNames = () => {
  const ent = getEntitlements();
  const names = Array.isArray(ent?.tab?.allowedTabNames) ? ent.tab.allowedTabNames : [];
  return names.map((n) => String(n).toLowerCase().trim()).filter(Boolean);
};

/**
 * Object names allowed for the current user (from session entitlements objectManager.allowedObjects).
 * Used by TabManager object-name dropdown: show only these + custom objects.
 */
export const getAllowedObjects = () => {
  const ent = getEntitlements();
  const list = Array.isArray(ent?.objectManager?.allowedObjects) ? ent.objectManager.allowedObjects : [];
  return list.map((n) => String(n).toLowerCase().trim()).filter(Boolean);
};

/**
 * Whether the current user is allowed to see the AI Chatbot.
 * Requires subscription + plan + license to allow 'aichatbot' in setup.allowedItems,
 * and role.permission_config.showAiChatbot not set to false.
 */
export const canShowAiChatbot = () => {
  const items = getEntitlements()?.setup?.allowedItems;
  if (!Array.isArray(items)) return false;
  return items.map((i) => String(i).toLowerCase()).includes('aichatbot');
};

/** True when public.standard_object.config.setup_object is set (boolean or common string forms). */
const isSetupObjectStandardConfig = (config) => {
  if (!config || typeof config !== 'object') return false;
  const v = config.setup_object;
  if (v === true) return true;
  if (typeof v === 'string') {
    const s = v.toLowerCase().trim();
    return s === 'true' || s === '1' || s === 'yes' || s === 't';
  }
  return false;
};

/** True when standard_object.config.assign_permission is false — hide from Setup → role object/record-view permissions. */
const isAssignPermissionDisabled = (config) => {
  if (!config || typeof config !== 'object') return false;
  const v = config.assign_permission;
  if (v === false) return true;
  if (typeof v === 'string' && v.toLowerCase().trim() === 'false') return true;
  return false;
};

const getStandardObjectConfigFromEntitlements = (configs, objectName) => {
  if (!objectName || !configs || typeof configs !== 'object') return null;
  if (configs[objectName]) return configs[objectName];
  const lower = String(objectName).toLowerCase();
  for (const k of Object.keys(configs)) {
    if (String(k).toLowerCase() === lower) return configs[k];
  }
  return null;
};

/**
 * Lowercase API names of standard objects with setup_object in session entitlements
 * (GET /api/auth/entitlements → standardObjectConfigs, same source as WorkflowEdit / Object Manager).
 */
export const getSetupStandardObjectNameLowerSet = () => {
  const configs = getEntitlementsSection('standardObjectConfigs', {}) || {};
  const set = new Set();
  for (const objectName of Object.keys(configs)) {
    if (objectName && isSetupObjectStandardConfig(configs[objectName])) {
      set.add(String(objectName).toLowerCase());
    }
  }
  return set;
};

/**
 * Drop standard objects that should not appear in Setup → role object permissions / record views:
 * - standard_object.config.setup_object (see isSetupObjectStandardConfig), or
 * - standard_object.config.assign_permission === false (omit from role permission UI; runtime API unchanged).
 * Custom objects are always kept. Uses session entitlements.standardObjectConfigs only.
 */
export const filterOutSetupStandardObjects = (permissions) => {
  if (!Array.isArray(permissions)) return [];
  const configs = getEntitlementsSection('standardObjectConfigs', {}) || {};
  return permissions.filter((p) => {
    if (p?.is_standard !== true) return true;
    const cfg = getStandardObjectConfigFromEntitlements(configs, p.object_name);
    if (!cfg) return true;
    if (isSetupObjectStandardConfig(cfg)) return false;
    if (isAssignPermissionDisabled(cfg)) return false;
    return true;
  });
};
